import {Component, computed, DestroyRef, inject, input, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {form, required, disabled, FormField} from '@angular/forms/signals';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {DataService} from '../../../../services/store/data.service';
import {EditType} from '../../../../classes/enums/EditType';
import {TemporaryPreferredTimeOff} from '../../../../classes/models/employee/TemporaryPreferredTimeOff';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {DatePickerStringAdapter} from '../../../../classes/adapters/datePickerStringAdapter';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {StoreWorkingTime} from '../../../../classes/models/store/StoreWorkingTime';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {
  EmployeeOneTimePreferredTimeOffService
} from '../../../../services/http/employee/employee-one-time-preferred-time-off.service';
import {
  TimeOffCommonFieldsModel,
  MIN_START_TIME,
  MAX_END_TIME,
  applyCommonFieldsValidation, TimeOffFormCommonFieldsComponent
} from '../time-off-form-common-fields/time-off-form-common-fields.component';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/** Signal form model for a temporary preferred time off. */
export interface TemporaryTimeOffFormModel {
  date: string | null;
  commonFields: TimeOffCommonFieldsModel;
}

/**
 * This component handles creating, editing and deleting a temporary preferred time off.
 */
@Component({
  selector: 'app-temporary-time-off-form',
  templateUrl: './temporary-time-off-form.component.html',
  styleUrls: ['./temporary-time-off-form.component.less'],
  imports: [SHARED_IMPORTS, TimeOffFormCommonFieldsComponent]
})
export class TemporaryTimeOffFormComponent implements OnInit {

  /** An edit action, which is performed by a user. */
  readonly editType = input<EditType>();
  /** Identifiers of an employee and position. */
  readonly employeePositionId = input<EmployeePositionId>();
  /** A temporary preferred time off to be edited. */
  readonly temporaryPreferredTimeOff = input<TemporaryPreferredTimeOff | undefined>();
  /** A modal component of a temporary preferred time off. */
  readonly modalComponent = input<ModalComponent>(undefined);
  /** Store earliest and latest working time for a week. */
  readonly storeWorkingTime = input<StoreWorkingTime>(undefined);

  private readonly destroyRef = inject(DestroyRef);
  private readonly employeeOneTimePreferredTimeOffService = inject(EmployeeOneTimePreferredTimeOffService);
  private readonly toastr = inject(ToastrService);
  private readonly ruleCheckerService = inject(RuleCheckerService);
  private readonly datePickerStringAdapter = inject(DatePickerStringAdapter);
  readonly dataService = inject(DataService);

  /** Assigning an EditType, so it could be used in HTML. */
  EditType = EditType;
  /** A computed signal for the first date from which a temporary preferred time off is allowed to be edited. */
  readonly editDate = computed(() => this.dataService.editDate);
  /** Signal tracking whether a request to backend is in progress. */
  readonly loading = signal(false);

  /** Signal form model for a temporary preferred time off. */
  readonly formModel = signal<TemporaryTimeOffFormModel>({
    date: null,
    commonFields: {
      allDay: false,
      startTime: '',
      endTime: '',
      reason: ''
    }
  });

  /** Signal form with validation schema for a temporary preferred time off. */
  readonly timeOffForm = form(this.formModel, (path) => {
    required(path.date);
    disabled(path.date, () => this.editType() === EditType.EDIT);
    applyCommonFieldsValidation(path.commonFields);
  });

  /**
   * Initializes the form model from component inputs on page initialization.
   */
  ngOnInit(): void {
    const prev = this.temporaryPreferredTimeOff();
    const openingTime = this.storeWorkingTime()?.openingTime ?? '';
    const closingTime = this.storeWorkingTime()?.closingTime ?? '';

    this.formModel.set({
      date: prev?.date ?? null,
      commonFields: {
        allDay: prev?.startTime === MIN_START_TIME && prev?.endTime === MAX_END_TIME,
        startTime: prev?.startTime ?? openingTime,
        endTime: prev?.endTime ?? closingTime,
        reason: prev?.reason ?? ''
      }
    });
  }

  /**
   * Converts editDate value to NgbDateStruct.
   */
  editDateAsNgbDate(): NgbDateStruct {
    return this.datePickerStringAdapter.fromModel(this.editDate());
  }

  /**
   * Sends create, edit or delete request to backend.
   * @param modalComponent a modal component of preferred time off.
   */
  onSubmit(modalComponent: ModalComponent): void {
    this.loading.set(true);

    const formValue = this.formModel();
    const preferredTimeOff = new TemporaryPreferredTimeOff(
            formValue.commonFields.reason,
            formValue.commonFields.startTime,
            formValue.commonFields.endTime,
            formValue.date
    );

    switch (this.editType()) {
      case EditType.CREATE:
        this.addTemporaryPreferredTimeOff(modalComponent, preferredTimeOff);
        break;
      case EditType.EDIT:
        this.editTemporaryPreferredTimeOff(modalComponent, preferredTimeOff);
        break;
      case EditType.DELETE:
        this.deleteTemporaryPreferredTimeOff(modalComponent, preferredTimeOff);
        break;
    }
  }

  private addTemporaryPreferredTimeOff(modalComponent: ModalComponent, preferredTimeOff: TemporaryPreferredTimeOff): void {
    this.employeeOneTimePreferredTimeOffService
            .addOneTimePreferredTimeOff(this.employeePositionId(), preferredTimeOff)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Temporary preferred time off successfully created');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private editTemporaryPreferredTimeOff(modalComponent: ModalComponent, preferredTimeOff: TemporaryPreferredTimeOff): void {
    this.employeeOneTimePreferredTimeOffService
            .editOneTimePreferredTimeOff(this.employeePositionId(), preferredTimeOff)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Temporary preferred time off successfully edited');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private deleteTemporaryPreferredTimeOff(modalComponent: ModalComponent, preferredTimeOff: TemporaryPreferredTimeOff): void {
    this.employeeOneTimePreferredTimeOffService
            .deleteOneTimePreferredTimeOff(this.employeePositionId(), preferredTimeOff.date)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Temporary preferred time off successfully deleted');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}